import { Dispatch } from 'react';
import { Action } from 'redux';

import { List } from 'modules/graphql/types';
import { ItemData } from 'ui/workflows/flowchart';
import { MessageMedium } from '../../constants';
import { WorkflowType } from '../../enums';
import type {
  BackendWorkflow,
  Campaign,
  CampaignId,
  ClonedFromJourney,
  ESFieldMappingDefinition,
  Edge,
  FbCustomAudienceByAccountId,
  JourneyVisitorMetricsResponse,
  MetricDefinitionsBySendAction,
  MetricInformation,
  Node,
  NodeClass,
  NodeData,
  NodeId,
  RestCampaign,
  Workflow,
} from '../../types';
import { WorkflowState } from '../ducks.types';
import * as actionTypes from './actionTypes';
import { WorkflowView } from 'modules/workflows/constants/workflow';

interface UniversalUpdateOptions {
  /** Suppress updating the `hasUnsavedChanges` prop */
  suppressHasUnsavedChangesUpdate?: boolean;
}

/*------------------------------------------------------------------------------------------------*/
/* Workflows Create Actions                                                                       */
/*------------------------------------------------------------------------------------------------*/

interface WorkflowResponsePayload {
  /** The workflow being fetched */
  workflow: BackendWorkflow;
  shouldFetchClonedFromJourney?: boolean;
  /** Should the nodes be validated when they're parsed */
  shouldValidateNodes?: boolean;
}

export interface CreateWorkflowDraftRequestAction
  extends Action<typeof actionTypes.CREATE_WORKFLOW_DRAFT_REQUEST> {
  payload: { workflow: Workflow };
}

export const createWorkflowDraftRequest = (
  workflow: Workflow,
): CreateWorkflowDraftRequestAction => ({
  type: actionTypes.CREATE_WORKFLOW_DRAFT_REQUEST,
  payload: { workflow },
});

export interface CreateWorkflowDraftResponseAction
  extends Action<typeof actionTypes.CREATE_WORKFLOW_DRAFT_RESPONSE> {
  payload: WorkflowResponsePayload;
}

export const createWorkflowDraftResponse = (
  workflow: Workflow,
): CreateWorkflowDraftResponseAction => ({
  type: actionTypes.CREATE_WORKFLOW_DRAFT_RESPONSE,
  payload: { workflow, shouldFetchClonedFromJourney: false, shouldValidateNodes: false },
});

/*------------------------------------------------------------------------------------------------*/
/* Workflows Fetch Actions                                                                        */
/*------------------------------------------------------------------------------------------------*/

export interface FetchWorkflowRequestAction
  extends Action<typeof actionTypes.FETCH_WORKFLOW_REQUEST> {
  payload: {
    workflowId: number;
    workflowType: WorkflowType;
    shouldFetchClonedFromJourney?: boolean;
  };
}

export const fetchWorkflowRequest = (
  workflowId: number,
  workflowType: WorkflowType,
  shouldFetchClonedFromJourney = false,
): FetchWorkflowRequestAction => ({
  type: actionTypes.FETCH_WORKFLOW_REQUEST,
  payload: { workflowId, workflowType, shouldFetchClonedFromJourney },
});

export interface FetchWorkflowResponseAction
  extends Action<typeof actionTypes.FETCH_WORKFLOW_RESPONSE> {
  payload: WorkflowResponsePayload;
}

export const fetchWorkflowResponse = (
  workflow: FetchWorkflowResponseAction['payload']['workflow'],
  {
    shouldFetchClonedFromJourney = false,
    shouldValidateNodes = false,
  }: Pick<WorkflowResponsePayload, 'shouldFetchClonedFromJourney' | 'shouldValidateNodes'> = {},
): FetchWorkflowResponseAction => ({
  type: actionTypes.FETCH_WORKFLOW_RESPONSE,
  payload: { workflow, shouldFetchClonedFromJourney, shouldValidateNodes },
});

export const fetchWorkflowError = (error: string) => ({
  type: actionTypes.FETCH_WORKFLOW_ERROR,
  error: true,
  payload: { error },
});

export interface FetchClonedJourneyResponseAction
  extends Action<typeof actionTypes.FETCH_CLONED_JOURNEY_RESPONSE> {
  payload: {
    clonedFromJourney: ClonedFromJourney;
  };
}

export const fetchClonedFromJourneyResponse = (
  clonedFromJourney: ClonedFromJourney,
): FetchClonedJourneyResponseAction => ({
  type: actionTypes.FETCH_CLONED_JOURNEY_RESPONSE,
  payload: { clonedFromJourney },
});

export const fetchClonedFromJourneyError = (error: string) => ({
  type: actionTypes.FETCH_CLONED_JOURNEY_ERROR,
  error: true,
  payload: { error },
});

/*------------------------------------------------------------------------------------------------*/
/* Workflows Update Actions                                                                       */
/*------------------------------------------------------------------------------------------------*/

export interface UpdateWorkflowAction extends Action<typeof actionTypes.UPDATE_WORKFLOW> {
  payload: {
    workflow: Partial<Workflow>;
    options?: UniversalUpdateOptions;
  };
}

export const updateWorkflow = (
  workflow: UpdateWorkflowAction['payload']['workflow'],
  options?: UpdateWorkflowAction['payload']['options'],
): UpdateWorkflowAction => ({
  type: actionTypes.UPDATE_WORKFLOW,
  payload: { workflow, options },
});

export interface UpdateHasUnsavedChangesAction
  extends Action<typeof actionTypes.UPDATE_HAS_UNSAVED_CHANGES> {
  payload: {
    hasUnsavedChanges: boolean;
  };
}

export const updateHasUnsavedChanges = (
  hasUnsavedChanges: boolean,
): UpdateHasUnsavedChangesAction => ({
  type: actionTypes.UPDATE_HAS_UNSAVED_CHANGES,
  payload: { hasUnsavedChanges },
});

export interface PublishWorkflowDraftRequestAction
  extends Action<typeof actionTypes.PUBLISH_WORKFLOW_DRAFT_REQUEST> {
  payload: {
    workflow: Workflow & { nodes: Node[]; edges: Edge[] };
  };
}

export const publishWorkflowDraftRequest = (
  workflow: PublishWorkflowDraftRequestAction['payload']['workflow'],
): PublishWorkflowDraftRequestAction => ({
  type: actionTypes.PUBLISH_WORKFLOW_DRAFT_REQUEST,
  payload: { workflow },
});

export interface SaveWorkflowDraftRequestAction
  extends Action<typeof actionTypes.SAVE_WORKFLOW_DRAFT_REQUEST> {
  payload: {
    workflow: Workflow & { nodes: Node[]; edges: Edge[] };
    goToWorkflow?: Partial<Workflow> & Required<Pick<Workflow, 'id' | 'workflowType'>>;
    onSuccess?: () => void;
  };
}

export const saveWorkflowDraftRequest = (
  workflow: SaveWorkflowDraftRequestAction['payload']['workflow'],
  goToWorkflow?: SaveWorkflowDraftRequestAction['payload']['goToWorkflow'],
  onSuccess?: () => void,
): SaveWorkflowDraftRequestAction => ({
  type: actionTypes.SAVE_WORKFLOW_DRAFT_REQUEST,
  payload: { workflow, goToWorkflow, onSuccess },
});

export interface SaveWorkflowDraftResponseAction
  extends Action<typeof actionTypes.SAVE_WORKFLOW_DRAFT_RESPONSE> {
  payload: WorkflowResponsePayload;
}

export const saveWorkflowDraftResponse = (
  workflow: SaveWorkflowDraftResponseAction['payload']['workflow'],
  {
    shouldFetchClonedFromJourney = false,
    shouldValidateNodes = false,
  }: Pick<WorkflowResponsePayload, 'shouldFetchClonedFromJourney' | 'shouldValidateNodes'> = {},
): SaveWorkflowDraftResponseAction => ({
  type: actionTypes.SAVE_WORKFLOW_DRAFT_RESPONSE,
  payload: { workflow, shouldFetchClonedFromJourney, shouldValidateNodes },
});

export const saveWorkflowError = (error?: string) => ({
  type: actionTypes.SAVE_WORKFLOW_ERROR,
  error: true,
  payload: { error },
});

export interface ResetWorkflowDraftRequestAction
  extends Action<typeof actionTypes.RESET_WORKFLOW_DRAFT_REQUEST> {
  payload: { workflowId: number; workflowType: WorkflowType };
}

export const resetWorkflowDraftRequest = (
  workflowId: number,
  workflowType: WorkflowType,
): ResetWorkflowDraftRequestAction => ({
  type: actionTypes.RESET_WORKFLOW_DRAFT_REQUEST,
  payload: { workflowId, workflowType },
});

export const resetWorkflowDraftError = (error?: string) => ({
  type: actionTypes.RESET_WORKFLOW_DRAFT_ERROR,
  error: true,
  payload: { error },
});

export interface UpdateClonedJourneyErrorNodesAction
  extends Action<typeof actionTypes.UPDATE_CLONED_JOURNEY_ERROR_NODES> {
  payload: {
    invalidNodes: string[];
  };
}

export const updateClonedJourneyErrorNodes = (
  invalidNodes: string[],
): UpdateClonedJourneyErrorNodesAction => ({
  type: actionTypes.UPDATE_CLONED_JOURNEY_ERROR_NODES,
  payload: { invalidNodes },
});

export interface RemoveClonedJourneyErrorNodeAction
  extends Action<typeof actionTypes.REMOVE_CLONED_JOURNEY_ERROR_NODE> {
  payload: {
    nodeId: string;
  };
}

export const removeClonedJourneyErrorNode = (
  nodeId: string,
): RemoveClonedJourneyErrorNodeAction => ({
  type: actionTypes.REMOVE_CLONED_JOURNEY_ERROR_NODE,
  payload: { nodeId },
});

export const fetchEventFieldMappingsRequest = () => ({
  type: actionTypes.FETCH_EVENT_FIELD_MAPPINGS_REQUEST,
  payload: {},
});

export const fetchEventFieldMappingsResponse = (mappings = {}) => ({
  type: actionTypes.FETCH_EVENT_FIELD_MAPPINGS_RESPONSE,
  payload: { mappings },
});

export const fetchEventFieldMappingsError = (error: string) => ({
  type: actionTypes.FETCH_EVENT_FIELD_MAPPINGS_ERROR,
  error: true,
  payload: { error },
});

/*------------------------------------------------------------------------------------------------*/
/* Workflows Other Actions                                                                        */
/*------------------------------------------------------------------------------------------------*/

export interface UpdateWorkflowMetricsAction
  extends Action<typeof actionTypes.UPDATE_WORKFLOW_METRICS> {
  payload: {
    workflow: Partial<Workflow>;
  };
}

export const updateWorkflowMetrics = (tileMetrics: {
  tileMetrics: MetricDefinitionsBySendAction;
}): UpdateWorkflowMetricsAction => ({
  type: actionTypes.UPDATE_WORKFLOW_METRICS,
  payload: { workflow: tileMetrics },
});

export interface UpdateWorkflowStatusRequestAction
  extends Action<typeof actionTypes.UPDATE_WORKFLOW_STATUS_REQUEST> {
  payload: {
    workflowIds: number[];
    enabled: boolean;
    shouldCancelAlerts?: boolean;
    shouldRescheduleAlerts?: boolean;
  };
}

export const updateWorkflowStatusRequest = (
  workflowIds: number[],
  enabled: boolean,
  shouldCancelAlerts?: boolean,
  shouldRescheduleAlerts?: boolean,
): UpdateWorkflowStatusRequestAction => ({
  type: actionTypes.UPDATE_WORKFLOW_STATUS_REQUEST,
  payload: { workflowIds, enabled, shouldCancelAlerts, shouldRescheduleAlerts },
});

export interface UpdateWorkflowStatusResponseAction
  extends Action<typeof actionTypes.UPDATE_WORKFLOW_STATUS_RESPONSE> {
  payload: {
    workflowId: number;
    enabled: boolean;
  };
}

export const updateWorkflowStatusResponse = (
  workflowId: number,
  enabled: boolean,
): UpdateWorkflowStatusResponseAction => ({
  type: actionTypes.UPDATE_WORKFLOW_STATUS_RESPONSE,
  payload: { workflowId, enabled },
});

export const updateWorkflowStatusError = (error: string) => ({
  type: actionTypes.UPDATE_WORKFLOW_STATUS_ERROR,
  error: true,
  payload: { error },
});

export interface SendTestTriggerRequestAction
  extends Action<typeof actionTypes.TEST_TRIGGER_REQUEST> {
  payload: {
    dataFields: object;
    email: string | null;
    userId: string | null;
    workflow: Workflow;
    triggerNode: Node;
    overrideSendLimit: boolean;
  };
}

export const sendTestTriggerRequest = (
  dataFields: object,
  email: string | null = null,
  userId: string | null = null,
  workflow: Workflow,
  triggerNode: Node,
  overrideSendLimit: boolean,
): SendTestTriggerRequestAction => ({
  type: actionTypes.TEST_TRIGGER_REQUEST,
  payload: { dataFields, email, userId, overrideSendLimit, triggerNode, workflow },
});

export const sendTestTriggerError = (error: string) => ({
  type: actionTypes.TEST_TRIGGER_ERROR,
  error: true,
  payload: { error },
});

export interface OpenModalAction extends Action<typeof actionTypes.OPEN_MODAL> {
  payload: {
    modalName: string;
    modalProps: Record<string, unknown>;
  };
}

export const openModal = (
  modalName: string,
  modalProps: Record<string, unknown>,
): OpenModalAction => ({
  type: actionTypes.OPEN_MODAL,
  payload: { modalName, modalProps },
});

export interface CloseModalAction extends Action<typeof actionTypes.CLOSE_MODAL> {
  payload: {
    modalName: string;
  };
}

export const closeModal = (modalName: string): CloseModalAction => ({
  type: actionTypes.CLOSE_MODAL,
  payload: { modalName },
});

export const openTakeover = (name: string, props: Record<string, any>) => ({
  type: actionTypes.OPEN_TAKEOVER,
  payload: { name, props },
});

export const closeTakeover = (name: string) => ({
  type: actionTypes.CLOSE_TAKEOVER,
  payload: { name },
});

export const fetchTopLevelCustomEventsRequest = () => ({
  type: actionTypes.FETCH_TOP_LEVEL_CUSTOM_EVENTS_REQUEST,
});

export const fetchTopLevelCustomEventsResponse = (topLevelCustomEvents: string[]) => ({
  type: actionTypes.FETCH_TOP_LEVEL_CUSTOM_EVENTS_RESPONSE,
  payload: { topLevelCustomEvents },
});

export const fetchTopLevelCustomEventsError = (error: string) => ({
  type: actionTypes.FETCH_TOP_LEVEL_CUSTOM_EVENTS_ERROR,
  error: true,
  payload: { error },
});

export const fetchCustomEventMappingsRequest = () => ({
  type: actionTypes.FETCH_CUSTOM_EVENT_MAPPINGS_REQUEST,
});

export const fetchCustomEventMappingsResponse = (
  customEventMappings: ESFieldMappingDefinition[] = [],
) => ({
  type: actionTypes.FETCH_CUSTOM_EVENT_MAPPINGS_RESPONSE,
  payload: { customEventMappings },
});

export const fetchCustomEventMappingsError = (error: string) => ({
  type: actionTypes.FETCH_CUSTOM_EVENT_MAPPINGS_ERROR,
  error: true,
  payload: { error },
});

export const fetchUserMappingsRequest = () => ({
  type: actionTypes.FETCH_USER_MAPPINGS_REQUEST,
});
export const fetchUserMappingsResponse = (userMappings: ESFieldMappingDefinition[] = []) => ({
  type: actionTypes.FETCH_USER_MAPPINGS_RESPONSE,
  payload: { userMappings },
});

export const fetchUserMappingsError = (error: string) => ({
  type: actionTypes.FETCH_USER_MAPPINGS_ERROR,
  error: true,
  payload: { error },
});

export interface FetchSendMetricsRequestAction
  extends Action<typeof actionTypes.FETCH_SEND_METRICS_REQUEST> {
  payload: {
    workflow: Workflow;
    dateRange?: WorkflowState['flexMetrics'];
  };
}

export const fetchSendMetricsRequest = (
  workflow: Workflow,
  dateRange?: Pick<WorkflowState['flexMetrics'], 'endDate' | 'startDate'>,
): FetchSendMetricsRequestAction => ({
  type: actionTypes.FETCH_SEND_METRICS_REQUEST,
  payload: { workflow, dateRange },
});

export interface FetchMetricsResponseAction
  extends Action<typeof actionTypes.FETCH_SEND_METRICS_RESPONSE> {
  payload: {
    campaignIdsToMetrics: Record<CampaignId, MetricInformation[]>;
    dateRange?: WorkflowState['flexMetrics'];
  };
}

export const fetchSendMetricsResponse = (
  campaignIdsToMetrics: FetchMetricsResponseAction['payload']['campaignIdsToMetrics'],
  dateRange?: FetchMetricsResponseAction['payload']['dateRange'],
) => ({
  type: actionTypes.FETCH_SEND_METRICS_RESPONSE,
  payload: { campaignIdsToMetrics, dateRange },
});

export interface FetchSendMetricsErrorAction
  extends Action<typeof actionTypes.FETCH_SEND_METRICS_ERROR> {
  error: true;
  payload: {
    error?: string;
  };
}

export const fetchSendMetricsError = (error?: string): FetchSendMetricsErrorAction => ({
  type: actionTypes.FETCH_SEND_METRICS_ERROR,
  // TODO: figure out why this is here, and if it can be removed.
  error: true,
  payload: { error },
});

interface CountData {
  workflowNodeId: number;
  count: number;
}

export interface FetchVisitorCountsRequestAction
  extends Action<typeof actionTypes.FETCH_VISITOR_COUNTS_REQUEST> {
  payload: {
    workflowId: number;
    workflowType: WorkflowType;
  };
}

export const fetchVisitorCountsRequest = (
  workflowId: number,
  workflowType: WorkflowType,
): FetchVisitorCountsRequestAction => ({
  type: actionTypes.FETCH_VISITOR_COUNTS_REQUEST,
  payload: { workflowId, workflowType },
});

export interface FetchVisitorCountsResponseAction
  extends Action<typeof actionTypes.FETCH_VISITOR_COUNTS_RESPONSE> {
  payload: {
    countsData: CountData[];
  };
}

// TODO: Type
export const fetchVisitorCountsResponse = (
  countsData: FetchVisitorCountsResponseAction['payload']['countsData'],
): FetchVisitorCountsResponseAction => ({
  type: actionTypes.FETCH_VISITOR_COUNTS_RESPONSE,
  payload: { countsData },
});

export const fetchVisitorCountsError = (error: string) => ({
  type: actionTypes.FETCH_VISITOR_COUNTS_ERROR,
  error: true,
  payload: { error },
});

export const fetchFbCustomAudienceRequest = () => ({
  type: actionTypes.FETCH_FB_CUSTOM_AUDIENCE_REQUEST,
});

export const fetchFbCustomAudienceResponse = (fbCustomAudience: FbCustomAudienceByAccountId) => ({
  type: actionTypes.FETCH_FB_CUSTOM_AUDIENCE_RESPONSE,
  payload: { fbCustomAudience },
});

export const fetchFbCustomAudienceError = (error: string) => ({
  type: actionTypes.FETCH_FB_CUSTOM_AUDIENCE_ERROR,
  error: true,
  payload: { error },
});

export type FetchJourneyVisitorMetricsRequestAction = {
  type: typeof actionTypes.FETCH_JOURNEY_VISITOR_METRICS_REQUEST;
  payload: {
    workflowId: number;
    dateRange: Pick<WorkflowState['flexMetrics'], 'endDateString' | 'startDateString'>;
  };
};

export const fetchJourneyVisitorMetricsRequest = (
  workflowId: number,
  dateRange: Pick<WorkflowState['flexMetrics'], 'endDateString' | 'startDateString'>,
) => ({
  type: actionTypes.FETCH_JOURNEY_VISITOR_METRICS_REQUEST,
  payload: {
    workflowId,
    dateRange,
  },
});

export type JourneyVisitorMetricsResponseAction = {
  type: Action<typeof actionTypes.FETCH_JOURNEY_VISITOR_METRICS_RESPONSE>;
  payload: { journeyVisitorMetrics: JourneyVisitorMetricsResponse };
};

export const fetchJourneyVisitorMetricsResponse = (
  journeyVisitorMetrics: JourneyVisitorMetricsResponse,
) => ({
  type: actionTypes.FETCH_JOURNEY_VISITOR_METRICS_RESPONSE,
  payload: { journeyVisitorMetrics },
});

interface UpsertWorkflowCampaignArgs {
  workflow: Partial<Workflow>;
  node: Node;
  campaign: Partial<RestCampaign>;
  messageMedium: MessageMedium;
  /** The node class of the upserted node, if you wish to override the default */
  nodeClass?: NodeClass;
  onFinish?: (nodeData: NodeData, newNodeId: NodeId, campaign: Partial<Campaign>) => void;
  onFailure?: () => void;
}

export interface UpsertWorkflowCampaignAction
  extends Action<typeof actionTypes.UPSERT_WORKFLOW_CAMPAIGN_REQUEST> {
  payload: UpsertWorkflowCampaignArgs;
}

export const upsertWorkflowCampaign = (
  args: UpsertWorkflowCampaignArgs,
): UpsertWorkflowCampaignAction => ({
  type: actionTypes.UPSERT_WORKFLOW_CAMPAIGN_REQUEST,
  payload: args,
});

export interface UpdateIsAsyncLoadingAction
  extends Action<typeof actionTypes.UPDATE_IS_ASYNC_LOADING> {
  payload: {
    isLoading: boolean;
  };
}

export const updateIsAsyncLoading = (isLoading: boolean): UpdateIsAsyncLoadingAction => ({
  type: actionTypes.UPDATE_IS_ASYNC_LOADING,
  payload: { isLoading },
});

export interface UpsertWorkflowCampaignLoadingAction
  extends Action<typeof actionTypes.UPSERT_WORKFLOW_CAMPAIGN_LOADING> {
  payload: {
    isLoading: boolean;
  };
}

export const isUpdatingCampaignDetails = (
  isLoading: boolean,
): UpsertWorkflowCampaignLoadingAction => ({
  type: actionTypes.UPSERT_WORKFLOW_CAMPAIGN_LOADING,
  payload: { isLoading },
});

export interface WorkflowViewAction extends Action<typeof actionTypes.UPDATE_WORKFLOW_VIEW> {
  payload: { workflowView: WorkflowView };
}
export const updateWorkflowView = (workflowView: WorkflowView) => ({
  type: actionTypes.UPDATE_WORKFLOW_VIEW,
  payload: { workflowView },
});
/*------------------------------------------------------------------------------------------------*/
/* Workflow Nodes Actions                                                                         */
/*------------------------------------------------------------------------------------------------*/

export interface AddNodeAction extends Action<typeof actionTypes.ADD_NODE> {
  payload: { node: Node; options?: UniversalUpdateOptions };
}

export const addNode = (node: Node, options?: UniversalUpdateOptions): AddNodeAction => ({
  type: actionTypes.ADD_NODE,
  payload: { node, options },
});

export interface AddNodesAction extends Action<typeof actionTypes.ADD_NODES> {
  payload: {
    // New nodes to not need an id -- the id will be added by the reducer
    nodes: Omit<Node, 'id'>[];
    options?: UniversalUpdateOptions;
  };
}

export const addNodes = (
  nodes: AddNodesAction['payload']['nodes'],
  options?: UniversalUpdateOptions,
): AddNodesAction => ({
  type: actionTypes.ADD_NODES,
  payload: { nodes, options },
});

export interface DeleteNodeAction extends Action<typeof actionTypes.DELETE_NODE> {
  payload: { nodeId: NodeId; options?: UniversalUpdateOptions };
}

export const deleteNode = (nodeId: NodeId, options?: UniversalUpdateOptions): DeleteNodeAction => ({
  type: actionTypes.DELETE_NODE,
  payload: { nodeId, options },
});

export interface DeleteNodesAction extends Action<typeof actionTypes.DELETE_NODES> {
  payload: { nodeIds: NodeId[]; options?: UniversalUpdateOptions };
}

export const deleteNodes = (nodeIds: NodeId[], options?: UniversalUpdateOptions) => ({
  type: actionTypes.DELETE_NODES,
  payload: { nodeIds, options },
});

export interface SelectNodesAction extends Action<typeof actionTypes.SELECT_NODES> {
  payload: { nodeIds: NodeId[] };
}

export const selectNodes = (nodeIds: NodeId[]): SelectNodesAction => ({
  type: actionTypes.SELECT_NODES,
  payload: { nodeIds },
});

export interface SelectGenAINodesAction extends Action<typeof actionTypes.SELECT_GEN_AI_NODES> {
  payload: { nodeIds: NodeId[] };
}

export const selectGenAINodes = (nodeIds: NodeId[]): SelectGenAINodesAction => ({
  type: actionTypes.SELECT_GEN_AI_NODES,
  payload: { nodeIds },
});

export interface DeselectNodesAction extends Action<typeof actionTypes.DESELECT_NODES> {
  payload: { nodeIds: NodeId[] };
}

export const deselectNodes = (nodeIds: NodeId[]): DeselectNodesAction => ({
  type: actionTypes.DESELECT_NODES,
  payload: { nodeIds },
});

export interface DeselectNodeAction extends Action<typeof actionTypes.DESELECT_NODE> {
  payload: { nodeId?: NodeId };
}

export const deselectNode = (nodeId?: NodeId): DeselectNodeAction => ({
  type: actionTypes.DESELECT_NODE,
  payload: { nodeId },
});

export interface UpdateNodeOptions extends UniversalUpdateOptions {
  /** Should the node be validated */
  shouldValidate?: boolean;
}

export interface UpdateNodeAction extends Action<typeof actionTypes.UPDATE_NODE> {
  payload: {
    /** Properties you wish to be updated in the node */
    node: Partial<Node>;
    /** Node id that you wish to update */
    nodeId?: NodeId;
    options?: UpdateNodeOptions;
  };
}

export const updateNode = (
  node: Partial<Node>,
  nodeId?: NodeId,
  options?: UniversalUpdateOptions,
): UpdateNodeAction => ({
  type: actionTypes.UPDATE_NODE,
  payload: { node, nodeId, options },
});

export interface UpdateNodesAction extends Action<typeof actionTypes.UPDATE_NODES> {
  payload: { nodes: Partial<Node>[]; options?: UpdateNodeOptions };
}

export const updateNodes = (
  nodes: UpdateNodesAction['payload']['nodes'],
  options?: UpdateNodeOptions,
): UpdateNodesAction => ({
  type: actionTypes.UPDATE_NODES,
  payload: { nodes, options },
});

export interface CloseEditorOptions {
  closeEditor?: boolean;
  [key: string]: any;
}

export interface UpdateNodeDataAction extends Action<typeof actionTypes.UPDATE_NODE_DATA> {
  payload: {
    nodeData: Partial<NodeData>;
    nodeId?: NodeId;
    options?: CloseEditorOptions & UpdateNodeOptions;
  };
}

export const updateNodeData = (
  nodeData: Partial<NodeData>,
  nodeId?: NodeId,
  options?: CloseEditorOptions,
): UpdateNodeDataAction => ({
  type: actionTypes.UPDATE_NODE_DATA,
  payload: { nodeData, options, nodeId },
});

export interface ReassignNodeIdAction extends Action<typeof actionTypes.REASSIGN_NODE_ID> {
  payload: { prevNodeId: NodeId; nodeId: NodeId };
}

export const reassignNodeId = (prevNodeId: NodeId, nodeId: NodeId): ReassignNodeIdAction => ({
  type: actionTypes.REASSIGN_NODE_ID,
  payload: { prevNodeId, nodeId },
});

export interface OpenNodeRequestAction extends Action<typeof actionTypes.OPEN_NODE_REQUEST> {
  payload: {
    nodeId: NodeId;
    nodeArgs?: Pick<ItemData, 'childNodeData'>;
  };
}

export const openNodeRequest = (
  nodeId: NodeId,
  nodeArgs?: Pick<ItemData, 'childNodeData'>,
): OpenNodeRequestAction => ({
  type: actionTypes.OPEN_NODE_REQUEST,
  payload: { nodeId, nodeArgs },
});

export interface OpenNodeResponseAction extends Action<typeof actionTypes.OPEN_NODE_RESPONSE> {
  payload: {
    // TODO: Type better
    resources: object;
    nodeId: NodeId;
    nodeUpdates: object;
  };
}

export const openNodeResponse = (
  resources: object,
  nodeId: NodeId,
  nodeUpdates: object,
): OpenNodeResponseAction => ({
  type: actionTypes.OPEN_NODE_RESPONSE,
  payload: { resources, nodeId, nodeUpdates },
});

export const openNodeError = (error: string) => ({
  type: actionTypes.OPEN_NODE_ERROR,
  error: true,
  payload: { error },
});

export const closeNode = () => ({
  type: actionTypes.CLOSE_NODE,
  payload: {},
});

export interface CloneNodesAction extends Action<typeof actionTypes.CLONE_NODES> {
  payload: { nodes: Node[]; options?: UniversalUpdateOptions };
}

export const cloneNodes = (nodes: Node[], options?: UniversalUpdateOptions): CloneNodesAction => ({
  type: actionTypes.CLONE_NODES,
  payload: { nodes, options },
});

export interface AddNodeErrorsAction extends Action<typeof actionTypes.ADD_NODE_ERRORS> {
  payload: { nodeIds: NodeId[]; hasError?: boolean };
}

export const addNodeErrors = (nodeIds: NodeId[], hasError = true): AddNodeErrorsAction => ({
  type: actionTypes.ADD_NODE_ERRORS,
  payload: { nodeIds, hasError },
});

export const removeNodeError = () => ({
  type: actionTypes.REMOVE_NODE_ERROR,
  payload: {},
});

export interface UpdateNodeComboIdAction extends Action<typeof actionTypes.UPDATE_NODE_COMBO_ID> {
  payload: { nodeIds: NodeId[]; comboId?: string; parentId?: string };
}

export const updateNodeComboId = (
  nodeIds: NodeId[],
  comboId?: string,
  parentId?: string,
): UpdateNodeComboIdAction => ({
  type: actionTypes.UPDATE_NODE_COMBO_ID,
  payload: { nodeIds, comboId, parentId },
});

/*------------------------------------------------------------------------------------------------*/
/* Workflow Edges Actions                                                                         */
/*------------------------------------------------------------------------------------------------*/

export interface AddEdgeAction extends Action<typeof actionTypes.ADD_EDGE> {
  payload: { edge: Edge; options?: UniversalUpdateOptions };
}

export const addEdge = (edge: Edge, options?: UniversalUpdateOptions): AddEdgeAction => ({
  type: actionTypes.ADD_EDGE,
  payload: { edge, options },
});

export interface AddEdgesAction extends Action<typeof actionTypes.ADD_EDGES> {
  payload: { edges: Edge[]; options?: UniversalUpdateOptions };
}

export const addEdges = (edges: Edge[], options?: UniversalUpdateOptions): AddEdgesAction => ({
  type: actionTypes.ADD_EDGES,
  payload: { edges, options },
});

export interface CloneEdgesAction extends Action<typeof actionTypes.CLONE_EDGES> {
  payload: { edges: Edge[] };
}

export const cloneEdges = (edges: Edge[]): CloneEdgesAction => ({
  type: actionTypes.CLONE_EDGES,
  payload: { edges },
});

export interface DeleteEdgeAction extends Action<typeof actionTypes.DELETE_EDGE> {
  payload: { edge: Edge; options?: UniversalUpdateOptions };
}

export const deleteEdge = (edge: Edge, options?: UniversalUpdateOptions): DeleteEdgeAction => ({
  type: actionTypes.DELETE_EDGE,
  payload: { edge, options },
});

export interface DeleteEdgesAction extends Action<typeof actionTypes.DELETE_EDGES> {
  payload: { edges: Edge[]; options?: UniversalUpdateOptions };
}

export const deleteEdges = (
  edges: Edge[],
  options?: UniversalUpdateOptions,
): DeleteEdgesAction => ({
  type: actionTypes.DELETE_EDGES,
  payload: { edges, options },
});

/*------------------------------------------------------------------------------------------------*/
/* Beta Management                                                                                */
/*------------------------------------------------------------------------------------------------*/

export const fetchBetaNodesRequest = () => ({
  type: actionTypes.FETCH_BETA_NODES_REQUEST,
  payload: {},
});

export interface FetchBetaNodesResponseAction
  extends Action<typeof actionTypes.FETCH_BETA_NODES_RESPONSE> {
  payload: {
    response: {
      supported: string[];
      unsupported?: string[];
    };
  };
}

export const fetchBetaNodesResponse = (
  response: FetchBetaNodesResponseAction['payload']['response'],
): FetchBetaNodesResponseAction => ({
  type: actionTypes.FETCH_BETA_NODES_RESPONSE,
  payload: { response },
});

export const fetchBetaNodesError = (error: string) => ({
  type: actionTypes.FETCH_BETA_NODES_ERROR,
  error: true,
  payload: { error },
});

/* ---------------------------------------------------------------------------------------------- */
/* Notes                                                                                          */
/* ---------------------------------------------------------------------------------------------- */

export const updateIsNotesFeatureEnabled = (isEnabled: boolean) => ({
  type: actionTypes.UPDATE_IS_NOTES_FEATURE_ENABLED,
  payload: { isEnabled },
});

export const updateIsNotesInlineEditEnabled = (isEnabled: boolean) => ({
  type: actionTypes.UPDATE_IS_NOTES_INLINE_EDIT_ENABLED,
  payload: { isEnabled },
});

export interface UpdateNotesVisibilityAction
  extends Action<typeof actionTypes.UPDATE_NOTES_VISIBILITY> {
  payload: {
    isVisible: boolean;
  };
}

export const updateNotesVisibility = (isVisible: boolean): UpdateNotesVisibilityAction => ({
  type: actionTypes.UPDATE_NOTES_VISIBILITY,
  payload: { isVisible },
});

export const showNotes = () => ({
  type: actionTypes.SHOW_NOTES,
  payload: {},
});

export const hideNotes = () => ({
  type: actionTypes.HIDE_NOTES,
  payload: {},
});

export const toggleNotesVisibility = () => ({
  type: actionTypes.TOGGLE_NOTES_VISIBILITY,
  payload: {},
});

/* -------------------------------------------------------------------------- */
/*                                Feature Flags                               */
/* -------------------------------------------------------------------------- */

export interface UpdateIsQuietHoursFeatureEnabledAction
  extends Action<typeof actionTypes.UPDATE_IS_QUIET_HOURS_FEATURE_ENABLED> {
  payload: {
    isEnabled: boolean;
  };
}

export const updateIsQuietHoursFeatureEnabled = (
  isEnabled: boolean,
): UpdateIsQuietHoursFeatureEnabledAction => ({
  type: actionTypes.UPDATE_IS_QUIET_HOURS_FEATURE_ENABLED,
  payload: { isEnabled },
});

export interface UpdateIsYesNoSplitDataToggleEnabledAction
  extends Action<typeof actionTypes.UPDATE_IS_YES_NO_SPLIT_DATA_TOGGLE_FEATURE_ENABLED> {
  payload: {
    isEnabled: boolean;
  };
}

export const updateIsYesNoSplitDataToggleEnabled = (
  isEnabled: boolean,
): UpdateIsYesNoSplitDataToggleEnabledAction => ({
  type: actionTypes.UPDATE_IS_YES_NO_SPLIT_DATA_TOGGLE_FEATURE_ENABLED,
  payload: { isEnabled },
});

export interface UpdateIsMetricsSelectionEnabledAction
  extends Action<typeof actionTypes.UPDATE_IS_METRICS_SELECTION_ENABLED> {
  payload: {
    isEnabled: boolean;
  };
}

export const updateIsMetricsSelectionEnabled = (
  isEnabled: boolean,
): UpdateIsMetricsSelectionEnabledAction => ({
  type: actionTypes.UPDATE_IS_METRICS_SELECTION_ENABLED,
  payload: { isEnabled },
});

export interface UpdateIsInProgressTotalPublishedViewEnabledAction
  extends Action<typeof actionTypes.UPDATE_IS_IN_PROGRESS_TOTAL_PUBLISHED_VIEW_ENABLED> {
  payload: {
    isEnabled: boolean;
  };
}

export const updateIsInProgressTotalPublishedViewEnabled = (
  isEnabled: boolean,
): UpdateIsInProgressTotalPublishedViewEnabledAction => ({
  type: actionTypes.UPDATE_IS_IN_PROGRESS_TOTAL_PUBLISHED_VIEW_ENABLED,
  payload: { isEnabled },
});

/* ---------------------------------------------------------------------------------------------- */
/* Tilesets                                                                                         */
/* ---------------------------------------------------------------------------------------------- */

export interface ImportTilesetAction extends Action<typeof actionTypes.IMPORT_TILESET> {
  payload: {
    nodes: Record<string, Node>;
    edges: Edge[];
    nextNodeId: number;
  };
}

export const importTileset = (tileset: ImportTilesetAction['payload']): ImportTilesetAction => ({
  type: actionTypes.IMPORT_TILESET,
  payload: { ...tileset },
});

/* ---------------------------------------------------------------------------------------------- */
/* Lists                                                                                        */
/* ---------------------------------------------------------------------------------------------- */

export interface CreateListPayload {
  /** The list title */
  title: string;
  /** The current project id */
  projectId: number;
  /** Callback for when the list is created */
  onListCreated?: (list: List) => void;
  /** Current dispatch function */
  dispatch: Dispatch<any>;
}

export interface CreateListAction extends Action<typeof actionTypes.CREATE_LIST_REQUEST> {
  payload: CreateListPayload;
}

export const createList = (payload: CreateListPayload): CreateListAction => ({
  type: actionTypes.CREATE_LIST_REQUEST,
  payload,
});

export interface UpdateProjectTimezoneAction
  extends Action<typeof actionTypes.UPDATE_PROJECT_TIMEZONE> {
  payload: { timeZone: string };
}

export const updateProjectTimeZone = (timeZone: string) => ({
  type: actionTypes.UPDATE_PROJECT_TIMEZONE,
  payload: { timeZone },
});
